// Client-side booth receipt (spec §8.10). WeasyPrint is server-only, so to hand
// the driver a document at issue time — including OFFLINE — we render a receipt
// in the browser and print it via a hidden iframe (no window.open, so no
// popup-blocker / user-activation issues). Online it carries the official
// invoice number; offline it is a clearly-marked PROVISIONAL receipt with a
// PENDING-<uuid> reference, reconciled to the official server PDF on sync.

export interface ReceiptLine {
  label: string;
  amount: string;
}

export interface ReceiptData {
  title: string;
  stationName: string;
  stationCode: string;
  operatorLabel: string;
  operator: string;
  dateLabel: string;
  dateText: string;
  vehicleLabel: string;
  plate: string;
  owner?: string | undefined;
  routeLabel: string;
  route: string;
  lines: ReceiptLine[];
  totalLabel: string;
  totalText: string;
  referenceLabel: string;
  referenceValue: string;
  /** Present only for offline/provisional receipts. */
  provisionalNote?: string | undefined;
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&quot;",
  );
}

export function buildReceiptHtml(d: ReceiptData): string {
  const rows = d.lines
    .map((l) => `<tr><td>${esc(l.label)}</td><td class="amt">${esc(l.amount)}</td></tr>`)
    .join("");
  const provisional = d.provisionalNote
    ? `<div class="provisional">${esc(d.provisionalNote)}</div>`
    : "";
  const ownerRow = d.owner
    ? `<div class="meta"><span>${esc(d.vehicleLabel)}</span><b>${esc(d.plate)} · ${esc(d.owner)}</b></div>`
    : `<div class="meta"><span>${esc(d.vehicleLabel)}</span><b>${esc(d.plate)}</b></div>`;

  return `<!doctype html><html><head><meta charset="utf-8" />
<style>
  @page { margin: 6mm; }
  * { box-sizing: border-box; }
  body { font: 12px/1.45 -apple-system, system-ui, sans-serif; color: #111; margin: 0; }
  .r { width: 72mm; margin: 0 auto; padding: 4mm 0; }
  h1 { font-size: 13px; text-align: center; margin: 0 0 1mm; }
  .sub { text-align: center; color: #555; font-size: 11px; margin-bottom: 3mm; }
  .meta { display: flex; justify-content: space-between; gap: 8px; padding: 1px 0; }
  .meta span { color: #555; }
  .meta b { text-align: right; }
  hr { border: 0; border-top: 1px dashed #999; margin: 3mm 0; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 1.5px 0; vertical-align: top; }
  td.amt { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .total { display: flex; justify-content: space-between; font-weight: 700; font-size: 14px; margin-top: 2mm; }
  .ref { margin-top: 3mm; text-align: center; font-variant-numeric: tabular-nums; }
  .ref span { color: #555; }
  .provisional { margin-top: 3mm; padding: 2mm; border: 1px solid #b45309; color: #b45309;
    text-align: center; font-weight: 600; font-size: 11px; border-radius: 3px; }
</style></head><body><div class="r">
  <h1>${esc(d.title)}</h1>
  <div class="sub">${esc(d.stationName)} (${esc(d.stationCode)})</div>
  <div class="meta"><span>${esc(d.dateLabel)}</span><b>${esc(d.dateText)}</b></div>
  <div class="meta"><span>${esc(d.operatorLabel)}</span><b>${esc(d.operator)}</b></div>
  ${ownerRow}
  <div class="meta"><span>${esc(d.routeLabel)}</span><b>${esc(d.route)}</b></div>
  <hr />
  <table>${rows}</table>
  <hr />
  <div class="total"><span>${esc(d.totalLabel)}</span><span>${esc(d.totalText)}</span></div>
  <div class="ref"><span>${esc(d.referenceLabel)}:</span> <b>${esc(d.referenceValue)}</b></div>
  ${provisional}
</div></body></html>`;
}

/** Print an HTML document via a hidden iframe (offline-safe, no popup). */
export function printHtml(html: string): void {
  if (typeof document === "undefined") {
    return;
  }
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const cleanup = () => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  };

  const win = iframe.contentWindow;
  const doc = win?.document;
  if (!win || !doc) {
    cleanup();
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();

  // Print once the iframe content is laid out; clean up after.
  const fire = () => {
    win.focus();
    win.print();
    setTimeout(cleanup, 1000);
  };
  if (doc.readyState === "complete") {
    setTimeout(fire, 100);
  } else {
    iframe.addEventListener("load", () => setTimeout(fire, 100), { once: true });
  }
}
